'use client'

import * as React from 'react'
import { AlertTriangle, Plus } from 'lucide-react'
import { addDoc, collection, getDocs, query, serverTimestamp, where } from 'firebase/firestore'
import { Badge } from 'components/ui/badge'
import { Button } from 'components/ui/button'
import { Card, CardContent, CardHeader, CardTitle } from 'components/ui/card'
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from 'components/ui/dialog'
import { Input } from 'components/ui/input'
import { Label } from 'components/ui/label'
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from 'components/ui/select'
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from 'components/ui/table'
import { Textarea } from 'components/ui/textarea'
import { toast } from 'sonner'
import { auth, db } from '../../../../firebase'
import { useProject } from './project-context'
import { useUser } from 'components/context/user-context'

function getRiskColor(score) {
  if (score >= 10) return 'bg-red-500'
  if (score >= 5) return 'bg-amber-500'
  return 'bg-emerald-500'
}
const AUDIT_COLLECTION = 'auditLogs'

export default function ProjectRisksPage() {
  const { project } = useProject()
  const { canEdit } = useUser()
  const [risks, setRisks] = React.useState([])
  const [isLoading, setIsLoading] = React.useState(true)
  const [open, setOpen] = React.useState(false)
  const [saving, setSaving] = React.useState(false)
  const [form, setForm] = React.useState({
    description: '',
    category: 'Operational',
    likelihood: '',
    impact: '',
    mitigationPlan: '',
    ownerName: '',
  })

  React.useEffect(() => {
    let isMounted = true

    async function loadRisks() {
      try {
        const risksQuery = query(collection(db, 'risks'), where('projectId', '==', project.id))
        const snapshot = await getDocs(risksQuery)
        if (!isMounted) return
        const records = snapshot.docs.map((docItem) => ({
          id: docItem.id,
          ...docItem.data(),
        }))
        setRisks(records)
      } catch {
        setRisks([])
      } finally {
        if (isMounted) setIsLoading(false)
      }
    }

    loadRisks()

    return () => {
      isMounted = false
    }
  }, [project.id])

  const handleSaveRisk = async () => {
    if (!form.description.trim()) {
      toast.error('Please enter a risk description.')
      return
    }
    const likelihood = Number(form.likelihood || 0)
    const impact = Number(form.impact || 0)
    if (!likelihood || !impact) {
      toast.error('Please select likelihood and impact.')
      return
    }
    const riskScore = likelihood * impact
    if (saving) return
    setSaving(true)
    try {
      const payload = {
        projectId: project.id,
        description: form.description.trim(),
        category: form.category || 'Operational',
        likelihood,
        impact,
        riskScore,
        mitigationPlan: form.mitigationPlan.trim(),
        ownerName: form.ownerName.trim() || 'Owner',
        status: 'open',
        createdAt: serverTimestamp(),
        updatedAt: serverTimestamp(),
      }
      const docRef = await addDoc(collection(db, 'risks'), payload)
      setRisks((prev) => [{ id: docRef.id, ...payload }, ...prev])
      try {
        const currentUser = auth.currentUser
        await addDoc(collection(db, AUDIT_COLLECTION), {
          timestamp: serverTimestamp(),
          userId: currentUser?.uid || 'system',
          userDisplayName: currentUser?.displayName || currentUser?.email || 'System',
          action: 'CREATE',
          entity: 'Project',
          entityId: project.id,
          changes: {
            before: '',
            after: `Added project risk "${payload.description}"`,
          },
        })
      } catch (error) {
        console.warn('Failed to write audit log', error)
      }
      toast.success('Risk added.')
      setForm({
        description: '',
        category: 'Operational',
        likelihood: '',
        impact: '',
        mitigationPlan: '',
        ownerName: '',
      })
      setOpen(false)
    } catch (error) {
      toast.error('Failed to add risk', {
        description: error?.message || 'Please try again.',
      })
    } finally {
      setSaving(false)
    }
  }

  return (
    <div className="space-y-6">
      <Card>
        <CardHeader className="flex flex-row items-center justify-between">
          <CardTitle className="text-base">Risk Register</CardTitle>
          {canEdit ? (
            <Dialog open={open} onOpenChange={setOpen}>
              <DialogTrigger asChild>
                <Button size="sm">
                  <Plus className="size-4 mr-1" />
                  Add Risk
                </Button>
              </DialogTrigger>
              <DialogContent className="max-h-[85vh] overflow-y-auto">
              <DialogHeader>
                <DialogTitle>Add New Risk</DialogTitle>
              </DialogHeader>
              <div className="space-y-4">
                <div className="space-y-2">
                  <Label>Description</Label>
                  <Textarea
                    value={form.description}
                    onChange={(event) => setForm((prev) => ({ ...prev, description: event.target.value }))}
                    placeholder="Describe the risk"
                  />
                </div>
                <div className="space-y-2">
                  <Label>Category</Label>
                  <Select
                    value={form.category}
                    onValueChange={(value) => setForm((prev) => ({ ...prev, category: value }))}
                  >
                    <SelectTrigger>
                      <SelectValue placeholder="Select category" />
                    </SelectTrigger>
                    <SelectContent>
                      {['Operational', 'Financial', 'Social', 'Environmental', 'Security', 'HR', 'Technical'].map(
                        (category) => (
                          <SelectItem key={category} value={category}>
                            {category}
                          </SelectItem>
                        ),
                      )}
                    </SelectContent>
                  </Select>
                </div>
                <div className="grid gap-3 sm:grid-cols-2">
                  <div className="space-y-2">
                    <Label>Likelihood (1-5)</Label>
                    <Select
                      value={form.likelihood}
                      onValueChange={(value) => setForm((prev) => ({ ...prev, likelihood: value }))}
                    >
                      <SelectTrigger>
                        <SelectValue placeholder="Score" />
                      </SelectTrigger>
                      <SelectContent>
                        {[1, 2, 3, 4, 5].map((value) => (
                          <SelectItem key={value} value={String(value)}>
                            {value}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                  <div className="space-y-2">
                    <Label>Impact (1-5)</Label>
                    <Select
                      value={form.impact}
                      onValueChange={(value) => setForm((prev) => ({ ...prev, impact: value }))}
                    >
                      <SelectTrigger>
                        <SelectValue placeholder="Score" />
                      </SelectTrigger>
                      <SelectContent>
                        {[1, 2, 3, 4, 5].map((value) => (
                          <SelectItem key={value} value={String(value)}>
                            {value}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                </div>
                <div className="space-y-2">
                  <Label>Mitigation Plan</Label>
                  <Textarea
                    value={form.mitigationPlan}
                    onChange={(event) => setForm((prev) => ({ ...prev, mitigationPlan: event.target.value }))}
                    placeholder="Describe mitigation strategy"
                  />
                </div>
                <div className="space-y-2">
                  <Label>Risk Owner</Label>
                  <Input
                    value={form.ownerName}
                    onChange={(event) => setForm((prev) => ({ ...prev, ownerName: event.target.value }))}
                    placeholder="Risk owner"
                  />
                </div>
                <Button className="w-full" onClick={handleSaveRisk} disabled={saving}>
                  {saving ? 'Saving...' : 'Save Risk'}
                </Button>
              </div>
              </DialogContent>
            </Dialog>
          ) : null}
        </CardHeader>
        <CardContent>
          <div className="overflow-x-auto">
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead className="w-10">Score</TableHead>
                  <TableHead>Description</TableHead>
                  <TableHead>Category</TableHead>
                  <TableHead className="text-center">L</TableHead>
                  <TableHead className="text-center">I</TableHead>
                  <TableHead>Mitigation</TableHead>
                  <TableHead>Owner</TableHead>
                  <TableHead>Status</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {isLoading ? (
                  <TableRow>
                    <TableCell colSpan={8} className="py-6 text-center text-sm text-muted-foreground">
                      Loading risks...
                    </TableCell>
                  </TableRow>
                ) : risks.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={8} className="py-6 text-center text-sm text-muted-foreground">
                      No risks logged yet.
                    </TableCell>
                  </TableRow>
                ) : (
                  risks.map((risk) => (
                    <TableRow key={risk.id}>
                      <TableCell>
                        <div className={`flex size-7 items-center justify-center rounded text-white ${getRiskColor(risk.riskScore || 0)}`}>
                          <AlertTriangle className="size-3.5" />
                        </div>
                      </TableCell>
                      <TableCell className="text-sm font-medium text-foreground max-w-[240px] truncate">
                        {risk.description || 'Risk'}
                      </TableCell>
                      <TableCell>
                        <Badge variant="outline" className="text-xs">
                          {risk.category || 'Operational'}
                        </Badge>
                      </TableCell>
                      <TableCell className="text-center text-sm">{risk.likelihood || '-'}</TableCell>
                      <TableCell className="text-center text-sm">{risk.impact || '-'}</TableCell>
                      <TableCell className="text-sm text-muted-foreground max-w-[200px] truncate">
                        {risk.mitigationPlan || '—'}
                      </TableCell>
                      <TableCell className="text-sm text-foreground">{risk.ownerName || 'Unassigned'}</TableCell>
                      <TableCell>
                        <Badge variant="secondary" className="text-[10px]">
                          {risk.status || 'open'}
                        </Badge>
                      </TableCell>
                    </TableRow>
                  ))
                )}
              </TableBody>
            </Table>
          </div>
        </CardContent>
      </Card>
    </div>
  )
}
